import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getUnreadNotificationCount } from '../services/notificationService';

type NotificationCountContextValue = {
  unreadCount: number | null;
  countUnavailable: boolean;
  isLoadingCount: boolean;
  reloadUnreadCount: () => Promise<void>;
  setUnreadCountAfterConfirmedRead: (nextCount: number) => void;
  syncUnreadCount: (count: number | null) => void;
  clearNotificationCount: () => void;
};

const NotificationCountContext =
  createContext<NotificationCountContextValue | null>(null);

function clampCount(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

export function NotificationCountProvider({
  children,
}: {
  children: ReactNode;
}) {
  const auth = useAuth();
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [countUnavailable, setCountUnavailable] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  const clearNotificationCount = useCallback(() => {
    setUnreadCount(null);
    setCountUnavailable(false);
    setIsLoadingCount(false);
  }, []);

  const syncUnreadCount = useCallback((count: number | null) => {
    if (count === null) {
      setCountUnavailable(true);
      return;
    }
    if (!Number.isInteger(count) || count < 0) {
      setCountUnavailable(true);
      return;
    }
    setUnreadCount(count);
    setCountUnavailable(false);
  }, []);

  const setUnreadCountAfterConfirmedRead = useCallback((nextCount: number) => {
    setUnreadCount(clampCount(nextCount));
    setCountUnavailable(false);
  }, []);

  const reloadUnreadCount = useCallback(async () => {
    setIsLoadingCount(true);
    try {
      const result = await getUnreadNotificationCount();
      setUnreadCount(result.unreadCount);
      setCountUnavailable(false);
    } catch {
      setCountUnavailable(true);
    } finally {
      setIsLoadingCount(false);
    }
  }, []);

  useEffect(() => {
    if (auth.status !== 'authenticated') {
      clearNotificationCount();
      return;
    }
    reloadUnreadCount().catch(() => undefined);
  }, [auth.status, clearNotificationCount, reloadUnreadCount]);

  const value = useMemo(
    () => ({
      unreadCount,
      countUnavailable,
      isLoadingCount,
      reloadUnreadCount,
      setUnreadCountAfterConfirmedRead,
      syncUnreadCount,
      clearNotificationCount,
    }),
    [
      unreadCount,
      countUnavailable,
      isLoadingCount,
      reloadUnreadCount,
      setUnreadCountAfterConfirmedRead,
      syncUnreadCount,
      clearNotificationCount,
    ],
  );

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  );
}

export function useNotificationCount(): NotificationCountContextValue {
  const value = useContext(NotificationCountContext);
  if (!value) {
    throw new Error(
      'useNotificationCount must be used within NotificationCountProvider',
    );
  }
  return value;
}

export function formatUnreadBadge(count: number | null): string | null {
  if (count === null || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}
